<script setup>
import { computed, ref } from 'vue';
import { useMessageContext } from '../../provider.js';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../../constants';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant } = useMessageContext();

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }

  return new MessageFormatter(props.content).formattedMessage;
});

// grinfi-patch-015: click-to-zoom for INLINE images. Pasted screenshots live
// inside the message HTML (not in attachments), so the attachment lightbox
// never saw them — clicking did nothing. Synthesize attachment-shaped objects
// from the <img> tags and reuse the stock GalleryView.
const showGallery = ref(false);
const inlineAttachments = ref([]);
const activeInlineAttachment = ref(null);

function onContentClick(e) {
  const img = e.target;
  if (!(img instanceof HTMLImageElement)) return;
  if (img.closest('a')) return; // linked images keep their link behavior
  const imgs = Array.from(e.currentTarget.querySelectorAll('img')).filter(
    el => !el.closest('a')
  );
  if (!imgs.length) return;
  inlineAttachments.value = imgs.map((el, index) => ({
    id: `inline-${index}`,
    // message_id doubles as the identity key GalleryView indexes by — the
    // per-image index keeps prev/next navigation working within the message.
    message_id: index,
    file_type: 'image',
    data_url: el.currentSrc || el.src,
  }));
  activeInlineAttachment.value =
    inlineAttachments.value[Math.max(imgs.indexOf(img), 0)];
  showGallery.value = true;
}
</script>

<template>
  <span
    v-dompurify-html="formattedContent"
    class="prose prose-bubble grinfi-inline-zoom"
    @click="onContentClick"
  />
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="activeInlineAttachment"
    :all-attachments="inlineAttachments"
    @close="() => (showGallery = false)"
  />
</template>

<style scoped>
.grinfi-inline-zoom :deep(img) {
  cursor: zoom-in;
}
</style>
